import type { AvatarProps } from '@mantine/core';
import { Avatar, useMantineTheme } from '@mantine/core';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { getUserInitialsFromName, pickColorType } from '@repo/common/utils';
import type { IconSizes } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { useWorkspace } from '../../api/hooks/workspace/useWorkspace';
import { isSecodaAIUser } from '../AIAssistant/utils';
import type { AvatarUser } from './common';
import { TooltipAvatar } from './common';
import { colorGroupToFillMap } from './helpers';

export type UserAvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const userAvatarSize: Record<UserAvatarSize, number> = {
	xxs: space[4],
	xs: space[5],
	sm: space[6],
	md: space[7],
	lg: space[8],
	xl: space[10],
} as const;

export interface UserAvatarProps {
	size: number | UserAvatarSize | IconSizes;
	user: AvatarUser;
	tooltip?: string;
	enableLink?: boolean;
	radius?: AvatarProps['radius'];
}

export function UserAvatar({
	user,
	tooltip,
	size,
	enableLink = false,
	radius = '3xs',
}: UserAvatarProps) {
	const { workspace } = useWorkspace();
	const theme = useMantineTheme();

	// Default xs is 16
	const convertedSize =
		typeof size === 'string'
			? (userAvatarSize[size as keyof typeof userAvatarSize] ?? 16)
			: size;

	if (isSecodaAIUser(user, workspace?.id ?? '')) {
		return <AIAvatar size={size as IconSizes} speed="none" />;
	}

	const colorGroup = pickColorType(user?.display_name, user?.disabled);
	const { fillStart, fillEnd, textColor } = colorGroupToFillMap(colorGroup);
	const initials = getUserInitialsFromName(user?.display_name) || '';

	return (
		<Avatar
			variant="filled"
			size={convertedSize}
			h={convertedSize}
			w={convertedSize}
			mih={convertedSize}
			miw={convertedSize}
			sx={{
				backgroundImage: theme.fn.gradient({
					from: fillStart,
					to: fillEnd,
					deg: 180,
				}),
				color: textColor,
			}}
			src={user?.profile_picture}
			alt={user?.display_name}
			component={TooltipAvatar}
			user={user}
			tooltip={tooltip}
			enableLink={enableLink}
			radius={radius}
		>
			<div style={{ fontSize: convertedSize / 2 }}>{initials}</div>
		</Avatar>
	);
}

export default UserAvatar;
